import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  model,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { MarkdownComponent, provideMarkdown } from 'ngx-markdown';
import { Checkbox } from '../checkbox/checkbox.component';
import { Color } from '../common/types';
import { Fieldset } from '../fieldset/fieldset.component';
import { Label } from '../label/label.component';

import { InputDirective } from '../input/input.directive';
import { AlertOptions, AlertResult } from './alert.type';

@Component({
  selector: 'lepi-alert',
  templateUrl: './alert.component.html',
  styleUrl: './alert.component.css',
  imports: [
    MarkdownComponent,
    CommonModule,
    InputDirective,
    Fieldset,
    Checkbox,
    Label,
  ],
  providers: [provideMarkdown()],
})
export class AlertComponent implements OnInit, AfterViewInit {
  readonly dialogRef = inject<DialogRef<AlertResult>>(DialogRef<AlertResult>);
  readonly dialogData = inject<AlertOptions>(DIALOG_DATA);

  modalContainer = viewChild<ElementRef<HTMLElement>>('modalContainer');

  scrollIndicator =
    viewChild.required<ElementRef<HTMLDivElement>>('scrollIndicator');

  formValues = signal<Record<string, any>>({});
  isFormInvalid = computed(() => {
    if (!this.dialogData.inputs) return false;
    return this.dialogData.inputs.some(input => {
      const isRequired = input.required || input.required !== false;
      if (!isRequired) return false;
      const value = this.formValues()[input.key];
      return value === undefined || value === null || value === '';
    });
  });

  checked = model<boolean>(false);
  checkInvalid = computed(() =>
    this.dialogData.checkbox ? !this.checked() : false
  );

  iconClass = computed(() => {
    let _icon = this.dialogData.icon?.name;
    if (_icon) {
      if (_icon.startsWith('icon-[')) return _icon;
      return `icon-[${_icon.replace(':', '--')}]`;
    }

    switch (this.dialogData.type) {
      case 'success':
        return 'icon-[material-symbols--check-circle]';
      case 'warning':
        return 'icon-[material-symbols--warning-rounded]';
      case 'error':
        return 'icon-[material-symbols--error]';
      case 'info':
        return 'icon-[material-symbols--info]';
      default:
        return 'icon-[material-symbols--info]';
    }
  });

  iconColor = computed<Color>(() => {
    return this.dialogData.type;
  });

  iconTypeClass = computed(() => {
    switch (this.dialogData.type) {
      case 'success':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'error':
        return 'text-error';
      case 'info':
        return 'text-primary';
    }
  });

  ngAfterViewInit() {
    if (!this.modalContainer()) return;

    this.modalContainer()?.nativeElement.animate(
      [
        { opacity: 0, transform: 'translateY(10px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      {
        duration: 200,
        easing: 'ease-in-out',
      }
    );
  }

  ngOnInit(): void {
    if (this.dialogData.inputs) {
      const initialValues: Record<string, any> = {};
      this.dialogData.inputs.forEach(input => {
        initialValues[input.key] = input.defaultValue ?? '';
      });
      this.formValues.set(initialValues);
    }
  }

  updateValue(key: string, value: any) {
    this.formValues.update(prev => ({ ...prev, [key]: value }));
  }

  onCancel() {
    this.dialogRef.close({
      action: 'cancel',
    });
  }

  onConfirm() {
    this.dialogRef.close({
      action: 'confirm',
      data: this.formValues(),
    });
  }

  onScroll(event: any) {
    const target = event.target;
    if (!target) return;
    const clientHeight = target.clientHeight;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;

    const threshold = scrollHeight - clientHeight - 100;

    const hasScrollLeft = threshold > scrollTop;

    this.scrollIndicator().nativeElement.style.opacity = hasScrollLeft
      ? '1'
      : '0';
  }
}
